import { API_BASE_URL } from '../config/runtime';
import { getActivePublishingTeamId } from '../utils/publishingIdentity';
import { pageCacheScope, pageCachePolicy, readPageResource, beginPageCacheWrite, finishPageCacheWrite, cancelPageCacheWrite, rotatePageCacheCredential } from '../services/pageCache';
import { WALLET_PATH } from '../services/pageCachePolicy';

const TOKEN_KEY = 'amposter_backend_session';
const REFRESH_TOKEN_KEY = 'amposter_backend_refresh';
const AUTH_CHANGED_EVENT = 'amposter:backend-auth-changed';
const DEFAULT_TIMEOUT_MS = 20000;
let csrfPromise = null;
let refreshPromise = null;
const inFlightReads = new Map();

export class ApiError extends Error {
  constructor(message, { status = 0, code = 'NETWORK_ERROR', details = null } = {}) {
    const language = typeof window === 'undefined' ? 'en' : window.localStorage.getItem('appLanguage') || 'ar';
    const localized = {
      MAX_ATTEMPTS_REACHED: { ar: 'لقد استنفدت جميع محاولاتك لهذه المسابقة.', en: 'You have used all attempts for this quiz.' },
      QUIZ_CLOSED: { ar: 'هذه المسابقة غير متاحة للعب حاليًا.', en: 'This quiz is not open right now.' },
      QUIZ_ATTEMPT_IN_PROGRESS: { ar: 'لديك محاولة قيد اللعب. أكملها قبل بدء محاولة جديدة.', en: 'Finish your active attempt before starting another one.' },
      QUIZ_TICKET_NOT_OWNED: { ar: 'ليس لديك تذكرة محاولات متاحة. يمكنك شراء واحدة من متجر الأدوات.', en: 'You have no quiz tickets available. You can buy one from the tools store.' },
      TOOL_PRICE_CHANGED: { ar: 'تغيّر سعر الأداة. راجع السعر الجديد ثم حاول مجددًا.', en: 'The tool price changed. Review the new price and try again.' },
      INSUFFICIENT_FUNDS: { ar: 'رصيدك غير كافٍ لإتمام عملية الشراء.', en: 'Your balance is too low to complete this purchase.' },
      INVALID_QUIZ_TICKET: { ar: 'اختر تذكرة محاولات صالحة.', en: 'Choose a valid quiz ticket.' },
      REQUEST_KEY_REUSED: { ar: 'تعذر تكرار الطلب لهذه العملية. أعد فتح الشاشة وحاول مجددًا.', en: 'This request belongs to another action. Reopen the screen and try again.' },
      FOLLOW_RATE_LIMITED: { ar: 'يمكنك متابعة 15 مستخدمًا خلال ثلاث دقائق. انتظر قليلًا قبل متابعة المزيد.', en: 'You can follow up to 15 users in three minutes. Please wait before following more.' },
      FOLLOW_IP_BLOCKED: { ar: 'تم إيقاف المتابعة مؤقتًا من هذه الشبكة بسبب تكرار الطلبات. يرجى الانتظار.', en: 'Following is temporarily paused from this network after repeated requests. Please wait.' },
      FOLLOW_PROTECTION_UNAVAILABLE: { ar: 'المتابعة غير متاحة مؤقتًا. حاول مجددًا بعد قليل.', en: 'Following is temporarily unavailable. Please try again shortly.' },
      MEDIA_JOB_LIMIT_REACHED: { ar: 'وصلت إلى حد عمليات رفع ومعالجة الفيديو المتزامنة. انتظر اكتمال الفيديوهات الحالية ثم أعد المحاولة.', en: 'The concurrent video upload limit has been reached. Wait for current videos to finish, then try again.' },
      UPLOAD_SIZE_MISMATCH: { ar: 'لم يصل الملف كاملًا أو حجمه لا يطابق الحجم المتوقع. أعد رفع الفيديو.', en: 'The uploaded file size does not match the expected size. Please upload the video again.' },
      UPLOAD_TOO_LARGE: { ar: 'حجم الملف يتجاوز الحد المسموح للفحص الأمني.', en: 'The file exceeds the safety scan size limit.' },
      COMMENT_GIFT_SELF: { ar: 'لا يمكنك إرسال هدية من المحفظة إلى منشورك.', en: 'You cannot send a wallet gift to your own post.' },
      ZIP_UPLOAD_DISABLED: { ar: 'رفع ملفات ZIP غير مسموح.', en: 'ZIP uploads are not supported.' },
      UPLOAD_UNSAFE: { ar: 'تم رفض الملف بواسطة فحص الأمان. يرجى اختيار ملف آخر.', en: 'This file was rejected by the safety scanner. Please choose another file.' },
      UPLOAD_SCAN_UNAVAILABLE: { ar: 'فحص أمان الملفات غير متاح مؤقتاً. يرجى المحاولة لاحقاً.', en: 'File safety checks are temporarily unavailable. Please try again later.' },
      UPLOAD_INVALID_CONTENT: { ar: 'محتوى الملف غير صالح أو لا يطابق نوعه أو يتجاوز حدود الأمان.', en: 'File contents are invalid, do not match the file type, or exceed safety limits.' },
      UNSUPPORTED_ATTACHMENT: { ar: 'نوع المرفق غير مدعوم. للمستندات استخدم PDF أو TXT أو DOCX أو XLSX أو PPTX.', en: 'Unsupported attachment. For documents use PDF, TXT, DOCX, XLSX, or PPTX.' },
      INVALID_IMAGE: { ar: 'الصورة غير صالحة أو أبعادها أكبر من المسموح.', en: 'The image is invalid or its dimensions exceed the limit.' },
      ACCOUNT_ACTION_RESTRICTED: { ar: 'هذا الإجراء مقيّد على حسابك حالياً.', en: 'This action is currently restricted for your account.' },
      ACCOUNT_FULLY_RESTRICTED: { ar: 'هذا الحساب مقيّد حالياً، ويمكنك فقط إرسال اعتراض.', en: 'This account is restricted; only a protest may be submitted.' },
      ACCOUNT_RESTRICTED: { ar: 'هذا الإجراء غير متاح مؤقتاً لهذا الحساب.', en: 'This action is temporarily unavailable for this account.' },
      COMMENTS_DISABLED: { ar: 'التعليقات مغلقة لهذا المنشور.', en: 'Comments are disabled for this post.' },
      REACTIONS_DISABLED: { ar: 'التفاعلات مغلقة لهذا المنشور.', en: 'Reactions are disabled for this post.' },
      TEAM_FIELD_RESTRICTED: { ar: 'هذا الحقل في الفريق مقيّد من قِبل الإدارة.', en: 'This team field is restricted by moderation.' },
    }[code];
    super(localized?.[language] || localized?.en || message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.retryAfterSeconds = Math.max(0, Number(details?.retryAfterSeconds) || 0);
  }
}

export const getBackendToken = () => localStorage.getItem(TOKEN_KEY);
export const getBackendRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const setBackendSession = (accessToken, refreshToken, { notify = true } = {}) => {
  const previousAccess = localStorage.getItem(TOKEN_KEY);
  const previousRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (accessToken) localStorage.setItem(TOKEN_KEY, accessToken);
  else localStorage.removeItem(TOKEN_KEY);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  else localStorage.removeItem(REFRESH_TOKEN_KEY);
  if (!notify || ((accessToken || null) === (previousAccess || null)
      && (refreshToken || null) === (previousRefresh || null))) return;
  window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT));
};

export const setBackendToken = (token) => {
  const previous = localStorage.getItem(TOKEN_KEY);
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
  if ((token || null) === (previous || null)) return;
  window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT));
};

export const clearBackendToken = ({ notify = true } = {}) => {
  if (notify) {
    setBackendSession(null, null);
    return;
  }
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};
export const backendAuthEventName = AUTH_CHANGED_EVENT;

const readBody = async (response) => {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try { return JSON.parse(text); } catch (_) { return { message: text }; }
  }
  return text;
};

export const refreshBackendSession = async () => {
  const refreshToken = getBackendRefreshToken();
  const previousAccessToken = getBackendToken();
  if (!refreshToken) return null;
  if (refreshPromise) return refreshPromise;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
  refreshPromise = fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'omit',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
    signal: controller.signal,
  }).then(async (response) => {
    const payload = await readBody(response);
    if (getBackendRefreshToken() !== refreshToken) {
      throw new ApiError('The active account changed', { code: 'SESSION_CHANGED' });
    }
    if (!response.ok || !payload?.accessToken || !payload?.refreshToken) {
      if ([400, 401, 403].includes(response.status)) clearBackendToken();
      throw new ApiError(payload?.message || 'Your session has expired', {
        status: response.status, code: payload?.code || 'SESSION_REFRESH_FAILED', details: payload,
      });
    }
    setBackendSession(payload.accessToken, payload.refreshToken, { notify: false });
    rotatePageCacheCredential(previousAccessToken, payload.accessToken);
    try {
      const registryKey = 'amposter:linked-backend-accounts:v1';
      const accounts = JSON.parse(localStorage.getItem(registryKey) || '[]');
      if (Array.isArray(accounts)) {
        let changed = false;
        const updated = accounts.map((account) => {
          if (account?.refreshToken !== refreshToken && account?.token !== previousAccessToken) return account;
          changed = true;
          return { ...account, token: payload.accessToken, refreshToken: payload.refreshToken };
        });
        if (changed) localStorage.setItem(registryKey, JSON.stringify(updated));
      }
    } catch (_) { /* a damaged optional account registry must not break authentication */ }
    return payload;
  }).catch((error) => {
    if (error?.name === 'AbortError') {
      throw new ApiError('The request timed out', { code: 'REQUEST_TIMEOUT' });
    }
    throw error instanceof ApiError ? error
      : new ApiError('The backend is unavailable', { code: 'NETWORK_ERROR', details: error });
  }).finally(() => {
    clearTimeout(timeout);
    refreshPromise = null;
  });
  return refreshPromise;
};

const fetchCsrf = async () => {
  if (!csrfPromise) {
    csrfPromise = fetch(`${API_BASE_URL}/api/v1/auth/csrf`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    }).then(async (response) => {
      if (!response.ok) throw new ApiError('Could not establish a secure session', { status: response.status, code: 'CSRF_FAILED' });
      return response.json();
    }).finally(() => { csrfPromise = null; });
  }
  return csrfPromise;
};

export const apiRequest = async (path, options = {}) => {
  const {
    method = 'GET', body, headers = {}, signal, timeoutMs = DEFAULT_TIMEOUT_MS,
    skipAuth = false, retryOnCsrf = true, retryAuth = true, notifyAuthFailure = true, deduplicate = true,
    usePublishingIdentity = false, credentials,
  } = options;
  // Capture the identity once, including across token refreshes and retries.
  const publishingTeamId = Object.hasOwn(options, '_publishingTeamId') ? options._publishingTeamId
    : skipAuth || !usePublishingIdentity ? null : getActivePublishingTeamId();
  options = { ...options, _publishingTeamId: publishingTeamId };
  const normalizedMethod = method.toUpperCase();
  let token = skipAuth ? null : getBackendToken();
  const scope = skipAuth ? null : pageCacheScope(token);
  if (scope && options.cache !== false && normalizedMethod === 'GET' && body === undefined && !signal && !Object.keys(headers).length && pageCachePolicy(path) && !(publishingTeamId && path.split('?')[0] === WALLET_PATH)) {
    const identity = publishingTeamId || 'default';
    const key = `${identity}:${path}`;
    // This layer owns deduplication. Bypass the older in-flight map so a read
    // after a mutation cannot reuse a pre-mutation request still in flight.
    return readPageResource(scope, key, path, () => apiRequest(path, { ...options, cache: false, deduplicate: false }), { reload: options.cache === 'reload' });
  }
  if (!skipAuth && !token && getBackendRefreshToken()) {
    const refreshed = await refreshBackendSession();
    token = refreshed?.accessToken || null;
  }

  // React Strict Mode deliberately mounts effects twice in development. Pages
  // can also share the same read (for example profile and notification badges).
  // Reuse only simultaneous, body-less GET requests; mutations, explicitly
  // abortable reads and later refreshes always execute independently.
  if (deduplicate && normalizedMethod === 'GET' && body === undefined && !signal) {
    const identityKey = publishingTeamId || 'default';
    const key = `${skipAuth ? 'public' : token || 'cookie'}:${identityKey}:${path}`;
    const existing = inFlightReads.get(key);
    if (existing) return existing;
    const request = apiRequest(path, { ...options, deduplicate: false });
    inFlightReads.set(key, request);
    request.finally(() => {
      if (inFlightReads.get(key) === request) inFlightReads.delete(key);
    }).catch(() => {});
    return request;
  }

  const requestHeaders = { Accept: 'application/json', ...headers };
  if (token) requestHeaders.Authorization = `Bearer ${token}`;
  if (publishingTeamId) requestHeaders['X-Amposter-Team-Id'] = publishingTeamId;
  if (body !== undefined && !(body instanceof FormData) && !requestHeaders['Content-Type']) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  // Explicit bearer/public requests must not inherit a previous Google session.
  // Cookie bootstrap and explicit cookie-clearing calls (logout) still include cookies.
  const requestCredentials = credentials ?? (token || skipAuth ? 'omit' : 'include');
  const unsafe = !['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(normalizedMethod);
  const requiresCsrf = unsafe && requestCredentials !== 'omit' && !token && !skipAuth;
  if (requiresCsrf) {
    const csrf = await fetchCsrf();
    requestHeaders[csrf.headerName || 'X-XSRF-TOKEN'] = csrf.token;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  const relayAbort = () => controller.abort();
  if (signal) signal.addEventListener('abort', relayAbort, { once: true });
  const mutation = unsafe ? beginPageCacheWrite(path, pageCacheScope(token), body) : null;

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: normalizedMethod,
      credentials: requestCredentials,
      headers: requestHeaders,
      body: body === undefined || body instanceof FormData ? body : JSON.stringify(body),
      signal: controller.signal,
    });
    const payload = await readBody(response);
    if (!response.ok) {
      if (response.status === 503 && payload?.code === 'MAINTENANCE_MODE') {
        window.dispatchEvent(new CustomEvent('amposter:maintenance-response', {
          detail: { enabled: true, message: payload.message, revision: payload.revision },
        }));
      }
      // Session restoration already owns the signed-out transition. Emitting
      // another auth-change event from that request can recursively start a
      // second restoration when an old bearer token is present.
      if (response.status === 401 && !skipAuth && token !== getBackendToken()) {
        throw new ApiError('The active account changed', { code: 'SESSION_CHANGED' });
      }
      if (response.status === 401 && !skipAuth && retryAuth && getBackendRefreshToken()) {
        try {
          await refreshBackendSession();
          cancelPageCacheWrite(mutation);
          return apiRequest(path, { ...options, retryAuth: false, deduplicate: false });
        } catch (refreshError) {
          if (['NETWORK_ERROR', 'REQUEST_TIMEOUT', 'SESSION_CHANGED'].includes(refreshError?.code)
              || refreshError?.status === 429 || refreshError?.status >= 500) throw refreshError;
          // Invalid refresh credentials fall through to the normal signed-out path.
        }
      }
      if (response.status === 401 && !skipAuth) {
        clearBackendToken({ notify: notifyAuthFailure });
      }
      if (response.status === 403 && retryOnCsrf && requiresCsrf) {
        cancelPageCacheWrite(mutation);
        return apiRequest(path, { ...options, retryOnCsrf: false });
      }
      throw new ApiError(payload?.message || `API request failed (${response.status})`, {
        status: response.status,
        code: payload?.code || `HTTP_${response.status}`,
        details: payload,
      });
    }
    if (unsafe) finishPageCacheWrite(mutation, path, payload);
    return payload;
  } catch (error) {
    // A timeout/5xx may follow a committed payment. Discard affected summaries
    // without retrying the mutation; the caller retains its idempotency key.
    if (unsafe && (!(error instanceof ApiError) || !error.status || error.status >= 500 || ['INSUFFICIENT_FUNDS', 'INSUFFICIENT_BALANCE'].includes(error.code))) {
      finishPageCacheWrite(mutation, path, null, { failed: true });
    }
    if (error instanceof ApiError) throw error;
    if (error?.name === 'AbortError') throw new ApiError('The request timed out', { code: 'REQUEST_TIMEOUT' });
    throw new ApiError('The backend is unavailable', { code: 'NETWORK_ERROR', details: error });
  } finally {
    cancelPageCacheWrite(mutation);
    clearTimeout(timeout);
    if (signal) signal.removeEventListener('abort', relayAbort);
  }
};

export const buildQuery = (values = {}) => {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};
